import type { Option } from "@tento-lona/sheets";
import type { DebounceExecutor, SheetHost } from "@tento-lona";

/** `SheetHost.themeBackend`'s type (`ThemesClient.Backend`). */
type ThemeBackend = NonNullable<SheetHost["themeBackend"]>;
import { TypedApi } from "@tento-core/typed-api";
import { $$ } from "@tento-ui/fastdom";
import { Tracing } from "@tento-core/tracing";
import { deepFrozen } from "@tento-lona/sheets/internal-ui";

const persistenceFlushers = new Set<() => void>();
let persistenceFlushInstalled = false;

/**
 * HTML DOM platform delegate — uses window dimensions for layout queries.
 * One per page and shared by every client on it, so frozen (its theme
 * backend too): a host that needs another delegate builds its own.
 */
export const DOM_HOST: SheetHost = Object.freeze<SheetHost>({
  installPersistenceFlush(flush) {
    // Wrapped, so installing the same function twice is two hooks and each
    // uninstall drops only its own.
    const hook = () => flush();
    persistenceFlushers.add(hook);
    if (!persistenceFlushInstalled) {
      persistenceFlushInstalled = true;
      addEventListener("pagehide", () => {
        Tracing.log("cell-persist/pagehide-flush", {
          flushers: persistenceFlushers.size,
        });
        for (const f of [...persistenceFlushers]) f();
      });
    }
    return () => {
      persistenceFlushers.delete(hook);
    };
  },
  getMaxHeight: () => window.innerHeight,
  getMaxWidth: () => window.innerWidth - 100,
  // ── Theme ──
  themeBackend: Object.freeze<ThemeBackend>({
    apply(theme) {
      $$.mutate(() => {
        if (!domHostThemeStyle) {
          domHostThemeStyle = document.createElement("style");
          domHostThemeStyle.id = "lona-theme-style";
          document.head.appendChild(domHostThemeStyle);
        }
        domHostThemeStyle.textContent =
          (theme as Option<{ asBaseCssString(): string }>)?.asBaseCssString() ??
          "";
      });
    },
  }),
});

let domHostThemeStyle: Option<HTMLStyleElement> = null;

/**
 * The page's debounce executor for `DebounceExecutors.browser(bridge)`: the
 * service worker (it coalesces and executes each debounced request) when
 * one controls the page, else a timer-based fallback that executes with a
 * direct fetch. A host's service-worker start replaces the installed
 * executor with the worker bridge once the worker takes control.
 */
export const DOM_DEBOUNCE_BRIDGE: DebounceExecutor.PageBridge = deepFrozen({
  install(baseUrl, accessCode) {
    if (
      typeof navigator !== "undefined" &&
      "serviceWorker" in navigator &&
      navigator.serviceWorker.controller
    ) {
      // Use service worker for debounce — it handles coalescing and fetch execution
      TypedApi.installDebounce((config) => {
        return new Promise<void>((resolve) => {
          const controller = navigator.serviceWorker.controller;
          if (controller == null) {
            void TypedApi.executeSerialized(config, baseUrl, accessCode, null).then(
              resolve,
            );
            return;
          }
          const ch = new MessageChannel();
          ch.port1.onmessage = (event) => {
            Tracing.info("sw")(event.data);
            resolve();
          };
          controller.postMessage({
            type: "debounce.queue",
            content: config,
          }, [ch.port2]);
        });
      });
    } else {
      // Fallback: setTimeout-based debounce with direct fetch
      const timers = new Map<string, ReturnType<typeof setTimeout>>();
      const completions = new Map<string, {
        promise: Promise<void>;
        resolve: () => void;
      }>();
      TypedApi.installDebounce((config) => {
        const key = config.customKey ?? `${config.method}:${config.route}`;
        let completion = completions.get(key);
        if (completion === undefined) {
          let resolveCompletion: () => void = () => {};
          const promise = new Promise<void>((resolve) => {
            resolveCompletion = resolve;
          });
          completion = { promise, resolve: resolveCompletion };
          completions.set(key, completion);
        }
        const existing = timers.get(key);
        if (existing) clearTimeout(existing);
        timers.set(
          key,
          setTimeout(async () => {
            timers.delete(key);
            await TypedApi.executeSerialized(config, baseUrl, accessCode, null);
            completions.get(key)?.resolve();
            completions.delete(key);
          }, config.durationMs),
        );
        return completion.promise;
      });
    }
  },
});
